/**
 * Custom Exercise Validation Schemas
 *
 * Zod schemas for validating custom exercise creation/editing.
 * Provides client-side validation that matches server-side validation rules.
 *
 * Features:
 * - Custom exercise form validation
 */

import {
  templateNameSchema,
  sessionDescriptionSchema,
  exerciseTypeSchema,
  difficultyLevelSchema,
} from './common.schemas';
import { z } from './zod';

// =============================================================================
// CUSTOM EXERCISE VALIDATION SCHEMAS
// =============================================================================

/**
 * Body part / muscle / equipment values are user-extensible: the form's
 * selectize fields are creatable and backed by `/api/selectize/{resource}`,
 * so any previously-used or newly-typed term is legitimate. Only presence and
 * length are validated client-side — NOT membership of the canonical enums in
 * `common.schemas` (those only cover the fixed external-catalog taxonomy).
 */
const freeTextTermSchema = (label: string): z.ZodString =>
  z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .max(100, `${label} must be less than 100 characters`);

/**
 * Custom exercise form validation schema
 */
export const customExerciseSchema = z.object({
  name: templateNameSchema,
  description: sessionDescriptionSchema,
  instructions: z.array(
    z
      .string()
      .min(1, 'Instructions cannot be empty')
      .max(500, 'Instructions must be less than 500 characters')
  ),
  body_part: freeTextTermSchema('Body part'),
  target: freeTextTermSchema('Primary muscle'),
  secondary_muscles: z
    .array(freeTextTermSchema('Secondary muscle'))
    .max(8, 'A maximum of 8 secondary muscles can be selected'),
  equipment: freeTextTermSchema('Equipment'),
  tracking_type: z
    .string()
    .min(1, 'Tracking type is required')
    .pipe(exerciseTypeSchema),
  difficulty_level: difficultyLevelSchema.optional(),
  is_public: z.boolean().optional().default(false),
});

/**
 * Update custom exercise schema (partial for PATCH requests).
 * zod 4 applies a default even inside .partial(), so the field's default is
 * unwrapped here: a key the caller leaves out stays out of the update.
 */
export const updateCustomExerciseSchema = customExerciseSchema
  .extend({ is_public: customExerciseSchema.shape.is_public.unwrap() })
  .partial();

// =============================================================================
// TYPE EXPORTS
// =============================================================================

// Custom exercise types
export type CustomExerciseInput = z.input<typeof customExerciseSchema>;
export type CustomExerciseOutput = z.output<typeof customExerciseSchema>;
export type UpdateCustomExerciseInput = z.input<
  typeof updateCustomExerciseSchema
>;
export type UpdateCustomExerciseOutput = z.output<
  typeof updateCustomExerciseSchema
>;

// =============================================================================
// VALIDATION HELPER FUNCTIONS
// =============================================================================

// --- Custom exercise validation ---

/**
 * Validate custom exercise data
 * @param data - The data to validate
 * @returns The validated and typed custom exercise data
 * @throws ZodError if validation fails
 */
export function validateCustomExercise(data: unknown): CustomExerciseOutput {
  return customExerciseSchema.parse(data);
}

/**
 * Safely validate custom exercise data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateCustomExercise(data: unknown) {
  return customExerciseSchema.safeParse(data);
}

/**
 * Check if data is valid custom exercise data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidCustomExercise(data: unknown): boolean {
  return customExerciseSchema.safeParse(data).success;
}

/**
 * Validate update custom exercise data
 * @param data - The data to validate
 * @returns The validated and typed update custom exercise data
 * @throws ZodError if validation fails
 */
export function validateUpdateCustomExercise(
  data: unknown
): UpdateCustomExerciseOutput {
  return updateCustomExerciseSchema.parse(data);
}

/**
 * Safely validate update custom exercise data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateUpdateCustomExercise(data: unknown) {
  return updateCustomExerciseSchema.safeParse(data);
}

/**
 * Check if data is valid update custom exercise data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidUpdateCustomExercise(data: unknown): boolean {
  return updateCustomExerciseSchema.safeParse(data).success;
}
